import React, { useEffect } from 'react';
import { FYNTRAC_MARK } from './FyntracMark';

/**
 * Floating "Fyn" launcher for the Ask Insight chat drawer.
 *
 * Mirrors the Fyntrac Copilot button in fyntrac-dsl (FyntracAssistantButton):
 * a navy roundel carrying the Fyn mark inside a teal / indigo / amber conic
 * ring that spins on hover, lifts slightly, and morphs into a close "×" as it
 * fades out of the way while the drawer is open (the drawer has its own close
 * button and Esc handling). Plain React + one injected <style>, so the
 * @property-driven ring animation works without MUI.
 *
 * Sits above page content and the sidebar but BELOW MUI modals (1300), so
 * dialogs and their action buttons are never covered by it.
 *
 * Props:
 *   open      – whether the chat drawer is open
 *   onToggle  – called with the next open state
 */
const css = `
@property --fy-spin { syntax: '<angle>'; initial-value: 0deg; inherits: false; }
.fy-fab{position:fixed;right:24px;bottom:24px;z-index:1250;width:60px;height:60px;border-radius:50%;padding:3px;border:0;cursor:pointer;
  background:conic-gradient(from var(--fy-spin),#0FA3A3,#4F46E5,#FCA311,#0FA3A3);
  box-shadow:0 10px 30px rgba(20,33,61,.22),0 2px 6px rgba(20,33,61,.12);
  transition:transform .22s cubic-bezier(.22,1,.36,1),opacity .18s ease}
.fy-fab:hover{transform:translateY(-2px) scale(1.04);animation:fy-spin 2.4s linear infinite}
.fy-fab:active{transform:scale(.96)}
.fy-fab:focus-visible{outline:3px solid #4F46E5;outline-offset:4px}
.fy-fab.open{opacity:0;pointer-events:none;transform:scale(.72) translateY(10px)}
.fy-core{width:100%;height:100%;border-radius:50%;background:#14213D;display:grid;place-items:center;position:relative;overflow:hidden}
.fy-core::after{content:"";position:absolute;inset:0;border-radius:50%;background:radial-gradient(120% 80% at 30% 15%,rgba(255,255,255,.18),transparent 55%)}
.fy-core svg{width:28px;height:28px;position:absolute;transition:transform .28s cubic-bezier(.3,1.4,.5,1),opacity .2s}
.fy-mark{width:38px!important;height:25px!important;margin-top:1px}
.fy-close{opacity:0;transform:rotate(-90deg) scale(.6)}
.fy-fab.open .fy-mark{opacity:0;transform:rotate(90deg) scale(.6)}
.fy-fab.open .fy-close{opacity:1;transform:none}
@keyframes fy-spin{to{--fy-spin:360deg}}
@media (max-width:600px){.fy-fab{right:16px;bottom:16px;width:52px;height:52px}.fy-mark{width:32px!important;height:21px!important}}
@media (prefers-reduced-motion:reduce){.fy-fab,.fy-core svg{transition:none}.fy-fab:hover{animation:none}}
@media print{.fy-fab{display:none}}
`;

function useStyle() {
  useEffect(() => {
    if (document.getElementById('fy-fab-style')) return;
    const s = document.createElement('style');
    s.id = 'fy-fab-style';
    s.textContent = css;
    document.head.appendChild(s);
  }, []);
}

const Mark = () => (
  <svg className="fy-mark" viewBox="0 0 160 104" aria-hidden="true">
    <image href={FYNTRAC_MARK} width="160" height="104" />
  </svg>
);

const Close = () => (
  <svg className="fy-close" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M7 7l10 10M17 7L7 17" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" fill="none" />
  </svg>
);

export default function FynLauncher({ open, onToggle }) {
  useStyle();
  const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);
  const shortcut = isMac ? '⌘ + Shift + A' : 'Ctrl + Shift + A';

  return (
    <button
      type="button"
      className={`fy-fab${open ? ' open' : ''}`}
      onClick={() => onToggle(!open)}
      tabIndex={open ? -1 : 0}
      aria-hidden={open ? 'true' : undefined}
      data-testid="fyn-launcher"
      aria-label={open ? 'Close Ask Insight' : 'Open Ask Insight'}
      aria-expanded={open}
      aria-haspopup="dialog"
      title={`Ask Insight (${shortcut})`}
    >
      <div className="fy-core">
        <Mark />
        <Close />
      </div>
    </button>
  );
}
